/**
 * Centralized social meta builder for Open Graph + Twitter Cards.
 * Use from a route's `head()` to emit a consistent, complete set of tags.
 */

export const SITE_URL = "https://coachquick.com";
export const DEFAULT_OG_IMAGE = `${SITE_URL}/coachquick-og.jpg`;
export const DEFAULT_OG_IMAGE_ALT = "CoachQuick — The all-in-one CRM for sports coaches";

export type SocialMetaInput = {
  /** Page title — used for <title>, og:title, twitter:title. */
  title: string;
  /** Page description — used for description, og:description, twitter:description. */
  description: string;
  /** Route path starting with "/". Combined with SITE_URL for og:url. */
  path: string;
  /** Absolute image URL (1200x630 recommended). Defaults to the shared brand card. */
  image?: string;
  /** Alt text for the image. */
  imageAlt?: string;
  /** og:type — default "website". Use "article" for blog posts, etc. */
  type?: "website" | "article" | "product" | "profile";
};

type MetaTag =
  | { title: string }
  | { name: string; content: string }
  | { property: string; content: string };

export function buildSocialMeta({
  title,
  description,
  path,
  image = DEFAULT_OG_IMAGE,
  imageAlt = DEFAULT_OG_IMAGE_ALT,
  type = "website",
}: SocialMetaInput): MetaTag[] {
  const url = `${SITE_URL}${path}`;
  return [
    { title },
    { name: "description", content: description },

    // Open Graph
    { property: "og:type", content: type },
    { property: "og:url", content: url },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:image", content: image },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { property: "og:image:alt", content: imageAlt },

    // Twitter Card
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
    { name: "twitter:image", content: image },
    { name: "twitter:image:alt", content: imageAlt },
  ];
}

/**
 * Meta for private/authenticated app pages. Sets a unique title + description,
 * adds noindex (these pages shouldn't appear in search), and emits a canonical.
 */
export function buildAppMeta({
  title,
  description,
  path,
}: {
  title: string;
  description: string;
  path: string;
}): MetaTag[] {
  return [
    { title },
    { name: "description", content: description },
    { name: "robots", content: "noindex, nofollow" },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:url", content: `${SITE_URL}${path}` },
  ];
}

